import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getFirestore, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { PullRequestDetails } from '@finvari/deliver';

// How many merged PRs to fetch. The workflow page narrows these to the ones not yet on the release
// branch, which Firestore cannot express as a query, so this bound is what keeps the read finite.
// Set well above a release cycle's worth of merges so the release backlog is never cut short by it.
const MERGED_LIMIT = 150;

interface PullRequestsContextType {
  pullRequests: PullRequestDetails[];
  // The most recently merged PRs, newest first — the pool the workflow page's Merged node draws its
  // release backlog from, and what its detail panel looks a merged PR up in. Open pages keep using
  // `pullRequests` only.
  mergedPrs: PullRequestDetails[];
  loading: boolean;
  error: string | null;
  lastRefreshed: number | null;
}

const PullRequestsContext = createContext<PullRequestsContextType | undefined>(undefined);

export const usePullRequests = () => {
  const context = useContext(PullRequestsContext);
  if (context === undefined) {
    throw new Error('usePullRequests must be used within a PullRequestsProvider');
  }
  return context;
};

interface PullRequestsProviderProps {
  children: ReactNode;
  user: User | null;
}

export const PullRequestsProvider: React.FC<PullRequestsProviderProps> = ({ children, user }) => {
  const [pullRequests, setPullRequests] = useState<PullRequestDetails[]>([]);
  const [mergedPrs, setMergedPrs] = useState<PullRequestDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null);

  useEffect(() => {
    // Only set up Firestore listener if user is authenticated
    if (!user) {
      setLoading(false);
      setPullRequests([]);
      setMergedPrs([]);
      setError(null);
      setLastRefreshed(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    const db = getFirestore();
    const pullRequestsRef = collection(db, 'pullRequests');
    
    // Only fetch open PRs — detail page for merged PRs won't work, but
    // environment pages use denormalized data from deployments, not this context.
    const q = query(pullRequestsRef, where('prStatus', '==', 'open'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const prs: PullRequestDetails[] = [];
      let latestRefreshed: number | null = null;
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const { lastRefreshed, refreshedBy, ...prData } = data;
        prs.push(prData as PullRequestDetails);
        if (lastRefreshed && (!latestRefreshed || lastRefreshed > latestRefreshed)) {
          latestRefreshed = lastRefreshed;
        }
      });
      
      // Sort the PRs by updated_at in descending order (excluding main branch)
      prs.sort((a, b) => {
        // Main branch (id: 0) should always be first
        if (a.pr.id === 0) return -1;
        if (b.pr.id === 0) return 1;
        
        // For other PRs, sort by updated_at
        const aDate = a.pr.updated_at ? new Date(a.pr.updated_at).getTime() : 0;
        const bDate = b.pr.updated_at ? new Date(b.pr.updated_at).getTime() : 0;
        return bDate - aDate;
      });
      
      setPullRequests(prs);
      setLastRefreshed(latestRefreshed);
      setLoading(false);
    }, (err) => {
      setError(err.message || 'Failed to load pull requests');
      setLoading(false);
    });

    // Second subscription: merged PRs for the workflow page's Merged node and detail panel. Kept out
    // of `pullRequests` so other pages (which only want open PRs) are unaffected. Newest first and
    // bounded by MERGED_LIMIT; which of these are still unreleased is decided against the release
    // state, since Firestore has no way to ask "not among these commits".
    const mergedQuery = query(
      pullRequestsRef,
      where('prStatus', '==', 'merged'),
      orderBy('pr.merged_at', 'desc'),
      limit(MERGED_LIMIT)
    );
    const unsubscribeMerged = onSnapshot(mergedQuery, (snapshot) => {
      const prs: PullRequestDetails[] = [];
      snapshot.docs.forEach(doc => {
        const { lastRefreshed, refreshedBy, ...prData } = doc.data();
        prs.push(prData as PullRequestDetails);
      });
      setMergedPrs(prs);
    }, (err) => {
      // Non-fatal for the page: the open list still renders. Log and move on.
      console.error('Failed to load merged pull requests', err);
    });

    return () => {
      unsubscribe();
      unsubscribeMerged();
    };
  }, [user]); // Now depends on user state

  const value: PullRequestsContextType = {
    pullRequests,
    mergedPrs,
    loading,
    error,
    lastRefreshed,
  };

  return (
    <PullRequestsContext.Provider value={value}>
      {children}
    </PullRequestsContext.Provider>
  );
}; 